import React, { useContext, useEffect, useState } from 'react';
import classes from './PostCardMediaPreview.module.scss';
import { MediaTypePostEditorState, PostEditorLink } from '../../../../models/post-editor';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import classNames from 'classnames';
import { PostCardMediaPreviewFacebook } from '../PostCardMediaPreviewFacebook';
import { CustomParagraph } from '../../../../models/slate-editor';
import { PostCardMediaPreviewInstagram } from '../PostCardMediaPreviewInstagram';
import { PostCardMediaPreviewFacebookProfile } from '../PostCardMediaPreviewFacebook/PostCardMediaPreviewFacebook.view';
import { PostCardMediaPreviewInstagramProfile } from '../PostCardMediaPreviewInstagram/PostCardMediaPreviewInstagram.view';
import { PostCardMediaPreviewPinterestProfile } from '../PostCardMediaPreviewPinterest/PostCardMediaPreviewPinterest.view';
import { PostCardMediaPreviewPinterest } from '../PostCardMediaPreviewPinterest';
import { PostCardMediaPreviewLinkedinProfile } from '../PostCardMediaPreviewLinkedin/PostCardMediaPreviewLinkedin.view';
import { PostCardMediaPreviewLinkedin } from '../PostCardMediaPreviewLinkedin';
import { PostCardMediaPreviewGoogleProfile } from '../PostCardMediaPreviewGoogle/PostCardMediaPreviewGoogle.view';
import { PostCardMediaPreviewGoogle } from '../PostCardMediaPreviewGoogle';
import { PostCardMediaPreviewTwitterProfile } from '../PostCardMediaPreviewTwitter/PostCardMediaPreviewTwitter.view';
import { PostCardMediaPreviewTwitter } from '../PostCardMediaPreviewTwitter';
import { PostCardMediaPreviewTikTokProfile } from '../PostCardMediaPreviewTikTok/PostCardMediaPreviewTikTok.view';
import { PostCardMediaPreviewTikTok } from '../PostCardMediaPreviewTikTok';
import { NotificationBoxes } from '../NotificationBoxes';
import { Tooltip } from '../../../../components/actions/Tooltip';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';
import { BeaconContext } from '../../../../providers/BeaconProvider';

export type PostCardPreviewMediaDataType = {
  link: PostEditorLink | null;
  firstComment: CustomParagraph[] | null;
  text: CustomParagraph[];
  media: MediaTypePostEditorState[];
};

export type PostCardMediaPreviewInstagramProps = Omit<
  PostCardMediaPreviewInstagramProfile,
  'hasVariations' | 'activeVariationIndex'
>;

export type PostCardMediaPreviewTikTokProps = Omit<
  PostCardMediaPreviewTikTokProfile,
  'hasVariations' | 'activeVariationIndex'
>;

export type PostCardMediaPreviewViewProps = (
  | PostCardMediaPreviewFacebookProfile
  | PostCardMediaPreviewInstagramProps
  | PostCardMediaPreviewPinterestProfile
  | PostCardMediaPreviewLinkedinProfile
  | PostCardMediaPreviewGoogleProfile
  | PostCardMediaPreviewTwitterProfile
  | PostCardMediaPreviewTikTokProps
) & {
  data: PostCardPreviewMediaDataType[];
  currentVariationIndex: number;
};
export const PostCardMediaPreviewView = (props: PostCardMediaPreviewViewProps): JSX.Element => {
  const { beacon } = useContext(BeaconContext);
  const { data, socialProfile, currentVariationIndex } = props;
  const [activeVariationIndex, setActiveVariationIndex] = useState<number>(currentVariationIndex);

  useEffect(() => {
    setActiveVariationIndex(currentVariationIndex);
  }, [currentVariationIndex]);

  const variationElement =
    data.length < 2 ? null : (
      <div
        className={classNames(classes.VariationsWrapper)}
        id={`#postPreviewId${activeVariationIndex + 1}-${socialProfile.__typename}-${socialProfile.id}`}>
        <div className={'grid-x align-middle'}>
          <div className={'cell shrink'}>
            <a
              onClick={() => setActiveVariationIndex(old => old - 1)}
              className={classes.VariationArrows}
              aria-disabled={activeVariationIndex === 0}>
              <Icon name={IconsEnum.arrowLeft} size={'medium'} />
            </a>
          </div>
          <div className={'cell shrink'}>
            <a
              onClick={() => setActiveVariationIndex(old => old + 1)}
              className={classNames(classes.VariationArrows, classes.VariationArrowsNext)}
              aria-disabled={activeVariationIndex === data.length - 1}>
              <Icon name={IconsEnum.arrowRight} size={'medium'} />
            </a>
          </div>
          <div className={classNames('cell', 'shrink', classes.VariationLabel)}>
            Variation {activeVariationIndex + 1} of {data.length}
          </div>
          <div className={classNames('cell', 'shrink', classes.TooltipWrapper)}>
            <Tooltip
              autoHide={false}
              autoZIndex={false}
              style={{ zIndex: +scssVars['z-index-overmodal'] + 1 }}
              pointer={true}
              type={'inlined'}
              target={`#postPreviewId${activeVariationIndex + 1}-${socialProfile.__typename}-${socialProfile.id}`}>
              <>
                Preview each Variation of your post to ensure they look just right.{' '}
                <a onClick={() => beacon('article', '687', { type: 'modal' })}>Learn more</a>
              </>
            </Tooltip>
          </div>
        </div>
      </div>
    );

  let previewContent: JSX.Element;
  const selectedVariation = data[activeVariationIndex] ?? data[currentVariationIndex];
  switch (socialProfile.__typename) {
    case 'FacebookSocialProfile':
      previewContent = (
        <PostCardMediaPreviewFacebook
          socialProfile={{ ...socialProfile }}
          media={selectedVariation.media}
          link={selectedVariation.link}
          text={selectedVariation.text}
          firstComment={selectedVariation.firstComment}
        />
      );
      break;
    case 'InstagramSocialProfile':
      if (!('igType' in props)) throw new Error('IG type not provided');
      if (!('publishMethod' in props)) throw new Error('publishMethod type not provided');
      previewContent = (
        <PostCardMediaPreviewInstagram
          socialProfile={{ ...socialProfile }}
          link={selectedVariation.link}
          firstComment={selectedVariation.firstComment}
          text={selectedVariation.text}
          media={selectedVariation.media}
          igType={props.igType}
          publishMethod={props.publishMethod}
          activeVariationIndex={activeVariationIndex}
          hasVariations={data.length > 1}
        />
      );
      break;
    case 'PinterestSocialProfile':
      previewContent = (
        <PostCardMediaPreviewPinterest
          link={selectedVariation.link}
          firstComment={selectedVariation.firstComment}
          text={selectedVariation.text}
          media={selectedVariation.media}
          socialProfile={{ ...socialProfile }}
          {...('selectedBoardId' in props ? { selectedBoardId: props.selectedBoardId } : {})}
          {...('title' in props ? { title: props.title } : {})}
        />
      );
      break;
    case 'LinkedInSocialProfile':
      previewContent = (
        <PostCardMediaPreviewLinkedin
          link={selectedVariation.link}
          firstComment={selectedVariation.firstComment}
          text={selectedVariation.text}
          media={selectedVariation.media}
          socialProfile={{ ...socialProfile }}
          {...('title' in props ? { title: props.title } : {})}
        />
      );
      break;
    case 'GoogleSocialProfile':
      if (!('buttonType' in props)) throw new Error('GoogleSocialProfile provided without buttonType');
      previewContent = (
        <PostCardMediaPreviewGoogle
          link={selectedVariation.link}
          firstComment={selectedVariation.firstComment}
          text={selectedVariation.text}
          media={selectedVariation.media}
          socialProfile={{ ...socialProfile }}
          buttonType={props.buttonType}
        />
      );
      break;
    case 'TikTokSocialProfile':
      if (!('privacy' in props)) throw new Error('tik tok privacy not provided');
      if (!('allowedActions' in props)) throw new Error('tik tok allowedActions not provided');
      previewContent = (
        <PostCardMediaPreviewTikTok
          link={selectedVariation.link}
          firstComment={selectedVariation.firstComment}
          text={selectedVariation.text}
          media={selectedVariation.media}
          socialProfile={{ ...socialProfile }}
          privacy={props.privacy}
          allowedActions={props.allowedActions}
        />
      );
      break;
    case 'TwitterSocialProfile':
      previewContent = (
        <PostCardMediaPreviewTwitter
          link={selectedVariation.link}
          firstComment={selectedVariation.firstComment}
          text={selectedVariation.text}
          media={selectedVariation.media}
          socialProfile={{ ...socialProfile }}
        />
      );
      break;
    default:
      previewContent = <></>;
  }
  const igType = 'igType' in props && socialProfile.__typename === 'InstagramSocialProfile' ? props.igType : undefined;
  const privacy = 'privacy' in props && socialProfile.__typename === 'TikTokSocialProfile' ? props.privacy : undefined;
  const allowedActions =
    'allowedActions' in props && socialProfile.__typename === 'TikTokSocialProfile' ? props.allowedActions : undefined;

  return (
    <>
      <div className={classNames(classes.PostCardMediaPreview)}>
        {variationElement}
        {previewContent}
      </div>
      <NotificationBoxes
        selectedVariation={selectedVariation}
        socialProfile={socialProfile}
        igType={igType}
        privacy={privacy}
        allowedActions={allowedActions}
      />
    </>
  );
};
